import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../contexts/ModalContext';
import EmptyState from '../components/EmptyState';
import {
  BuildingOfficeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentCheckIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { Search as SearchIcon } from 'lucide-react';
import { RowActionsMenu, type RowAction } from '../components/ui/RowActionsMenu';
import { useToast } from '../contexts/ToastContext';
import { useModal as useModalCtx } from '../contexts/ModalContext';
import { sitesApi } from '../utils/api';
import { useQueryClient } from '@tanstack/react-query';

type ComplianceStatus = 'Compliant' | 'At risk' | 'Non-compliant';
type SiteStatus = 'Active' | 'Inactive';
type PropertyType = 'Domestic' | 'Commercial';

type SiteRow = {
  id: string;
  address: string;
  clientName: string;
  propertyType: PropertyType;
  appliances: number;
  lastService: string;
  nextDue: string;
  complianceStatus: ComplianceStatus;
  status: SiteStatus;
};

const getComplianceBadgeClass = (status: ComplianceStatus) => {
  if (status === 'Compliant') return 'status-success';
  if (status === 'At risk') return 'status-warning';
  return 'status-danger';
};

const getStatusBadgeClass = (status: SiteStatus) => {
  if (status === 'Active') return 'status-success';
  return 'status-danger';
};

export default function Sites() {
  const { openModal } = useModal();
  const modalCtx = useModalCtx();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [sites] = useState<SiteRow[]>([]);
  const [filteredSites, setFilteredSites] = useState<SiteRow[]>([]);

  const buildRowActions = (site: SiteRow): RowAction[] => [
    {
      id: 'view',
      label: 'View site',
      icon: EyeIcon,
      onClick: () => navigate(`/dashboard/sites/${site.id}`),
    },
    {
      id: 'edit',
      label: 'Edit details',
      icon: PencilIcon,
      onClick: () =>
        modalCtx.openModal('createSite', {
          initialData: { id: site.id, address: site.address, mode: 'edit' },
        }),
    },
    {
      id: 'new-job',
      label: 'Create job at site',
      icon: ClipboardDocumentListIcon,
      divider: true,
      onClick: () =>
        modalCtx.openModal('createJob', {
          initialData: { siteId: site.id, siteAddress: site.address },
        }),
    },
    {
      id: 'new-cert',
      label: 'Issue certificate',
      icon: DocumentCheckIcon,
      onClick: () =>
        modalCtx.openModal('createCertificate', {
          initialData: { siteId: site.id, siteAddress: site.address },
        }),
    },
    {
      id: 'schedule',
      label: 'Schedule next service',
      icon: CalendarDaysIcon,
      onClick: () =>
        modalCtx.openModal('createJob', {
          initialData: {
            siteId: site.id,
            siteAddress: site.address,
            jobType: 'SERVICE',
          },
        }),
    },
    {
      id: 'delete',
      label: 'Delete site',
      icon: TrashIcon,
      danger: true,
      divider: true,
      onClick: async () => {
        if (!window.confirm(`Delete site "${site.address}"? This cannot be undone.`)) return;
        try {
          await sitesApi.delete(site.id);
          await queryClient.invalidateQueries({ queryKey: ['sites'] });
          showToast('Site deleted', 'success');
        } catch (err: any) {
          showToast(err?.response?.data?.message || 'Failed to delete site', 'error');
        }
      },
    },
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    let filtered = [...sites];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((site) =>
        site.address.toLowerCase().includes(q) ||
        site.clientName.toLowerCase().includes(q) ||
        site.propertyType.toLowerCase().includes(q)
      );
    }

    setFilteredSites(filtered);
    setCurrentPage(1);
  }, [sites, searchTerm]);

  const sitesToShow = useMemo(
    () => (searchTerm ? filteredSites : sites),
    [filteredSites, searchTerm, sites]
  );

  const totalPages = Math.max(1, Math.ceil(sitesToShow.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSites = sitesToShow.slice(startIndex, startIndex + itemsPerPage);

  if (sites.length === 0) {
    return (
      <div className="page-container">
        <div className="mb-6 border-b border-[#E6E6E4] pb-3">
          <div className="page-header">
            <div>
              <h1 className="text-2xl font-semibold text-[#191919] leading-tight">Sites</h1>
              <p className="mt-1 text-xs font-normal text-[#515959]">
                Manage properties, service locations, and site-specific records.
              </p>
            </div>
            <div className="header-actions">
              <div className="relative w-full max-w-xs">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#686E6C]" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search sites…"
                  className="h-9 w-full rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] pl-9 pr-3 text-sm text-[#383838] placeholder:text-[#686E6C] focus:outline-none focus:ring-2 focus:ring-[#CDCDCD]/80"
                />
              </div>
              <button
                type="button"
                onClick={() => openModal('createSite')}
                className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] px-3 py-2 text-sm font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add site
              </button>
            </div>
          </div>
        </div>
        <EmptyState
          icon={BuildingOfficeIcon}
          title="No sites yet"
          description="Add your first site to start tracking properties, compliance, and service history."
          actionLabel="Add site"
          onAction={() => openModal('createSite')}
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="mb-6 border-b border-[#E6E6E4] pb-3">
        <div className="page-header">
          <div>
            <h1 className="text-2xl font-semibold text-[#191919] leading-tight">Sites</h1>
            <p className="mt-1 text-xs font-normal text-[#515959]">
              Manage properties, service locations, and site-specific records.
            </p>
          </div>

          <div className="header-actions">
            <div className="relative w-full max-w-xs">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#686E6C]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search sites…"
                className="h-9 w-full rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] pl-9 pr-3 text-sm text-[#383838] placeholder:text-[#686E6C] focus:outline-none focus:ring-2 focus:ring-[#CDCDCD]/80"
              />
            </div>

            <button
              type="button"
              onClick={() => openModal('createSite')}
              className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] px-3 py-2 text-sm font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add site
            </button>
          </div>
        </div>
      </div>

      <div className="table-container">
        <div className="table-wrapper">
          <table className="transactions-table">
            <thead className="table-header">
              <tr>
                <th className="col-checkbox">
                  <input type="checkbox" className="table-checkbox" />
                </th>
                <th className="col-description">Address</th>
                <th className="col-account">Client</th>
                <th className="col-amount">Property type</th>
                <th className="col-amount">Appliances</th>
                <th className="col-date">Last service</th>
                <th className="col-date">Next due</th>
                <th className="col-payment">Compliance status</th>
                <th className="col-payment">Status</th>
                <th className="col-actions" />
              </tr>
            </thead>
            <tbody>
              {paginatedSites.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-8">
                    <div className="text-gray-500">
                      <BuildingOfficeIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="font-medium mb-1">No sites found</p>
                      <p className="text-sm">Try adjusting your search</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedSites.map((site) => (
                  <tr
                    key={site.id}
                    className="table-row"
                    onClick={() => navigate(`/dashboard/sites/${site.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="col-checkbox" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="table-checkbox" />
                    </td>
                    <td className="col-description">
                      <div className="transaction-description">
                        <div className="transaction-icon-wrapper">
                          <BuildingOfficeIcon className="w-5 h-5" style={{ color: '#6B7280' }} />
                        </div>
                        <div className="transaction-details">
                          <h4>{site.address}</h4>
                          <p>{site.id.toUpperCase()}</p>
                        </div>
                      </div>
                    </td>
                    <td className="col-account">
                      <span className="text-sm">{site.clientName}</span>
                    </td>
                    <td className="col-amount">
                      <span className="text-sm">{site.propertyType}</span>
                    </td>
                    <td className="col-amount">
                      <span className="status-badge pending">{site.appliances}</span>
                    </td>
                    <td className="col-date">
                      <span className="text-sm">{site.lastService}</span>
                    </td>
                    <td className="col-date">
                      <span className="text-sm">{site.nextDue}</span>
                    </td>
                    <td className="col-payment">
                      <span className={`status-badge ${getComplianceBadgeClass(site.complianceStatus)}`}>
                        {site.complianceStatus}
                      </span>
                    </td>
                    <td className="col-payment">
                      <span className={`status-badge ${getStatusBadgeClass(site.status)}`}>
                        {site.status}
                      </span>
                    </td>
                    <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu actions={buildRowActions(site)} label="Site actions" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <div className="pagination-info">
            Page {currentPage} of {totalPages} • {sitesToShow.length} sites
            {searchTerm && (
              <span className="text-gray-500 ml-2">(filtered from {sites.length} total)</span>
            )}
          </div>
          <div className="pagination-controls">
            <button
              className="page-btn"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              type="button"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
                type="button"
              >
                {i + 1}
              </button>
            ))}

            <button
              className="page-btn"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              type="button"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
